import React from 'react';
import Button from 'epn-ui/umd/dist/components/Button';
import Select from 'epn-ui/umd/dist/components/Select';
import Chips from 'epn-ui/umd/dist/components/Chips';
import isEmpty from 'lodash/isEmpty';

import { faCrown } from '@fortawesome/pro-regular-svg-icons/faCrown';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import Switch from 'epn-ui/umd/dist/components/Switch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import BaseEmpty from '@components/base/BaseEmpty';
import useI18n from '@i18n';
import { selectsConfig } from '@pages/coupons/config';
import { ICouponFilters } from '@pages/coupons/interfaces';

const Filters: React.FC<ICouponFilters> = ({
    values,
    onChange,
    selectsOptions,
    loadings,
    resetFilters,
}) => {
    const { t } = useI18n('Coupons');

    const { offers, types } = selectsOptions;
    const { offerId, typeId, isPersonal, withPromocode } = values;

    const valuesConfig = {
        offerId: {
            options: offers,
            valueId: offerId,
        },
        typeId: {
            options: types,
            valueId: typeId,
        },
    };

    const SelectFilter = (inputValue, option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase());

    return (
        <div className="p-0 mb-6 sm:p-[24px] rounded-[6px] shadow-0 xs:shadow-5 md:p-[32px]">
            <div className="flex flex-col mb-[16px] sm:mb-[24px] md:mb-[32px] sm:flex-row">
                {selectsConfig.map(({ id, type, isSearchable, placeholder }) => {
                    const { valueId: value, options } = valuesConfig[type];

                    // TODO remove "as" when TypeScript gets updated to modern version
                    const comboboxValue =
                        (options as Array<typeof valuesConfig[typeof type]['options'][0]>).find(
                            option => option.value?.toString() === value?.toString(),
                        ) || null;
                    return (
                        <div
                            key={type}
                            className="w-full mr-0 mb-[16px] sm:mb-0 sm:mr-[24px] last:mb-0 sm:last:mr-0 md:mr-[32px] md:last:mr-0"
                        >
                            <Select
                                id={id}
                                value={comboboxValue}
                                loading={loadings[type]}
                                onChange={val => onChange(type, val)}
                                options={options}
                                placeholder={t(placeholder)}
                                filterOption={isSearchable ? SelectFilter : undefined}
                                showSearch
                                notFoundContent={<BaseEmpty />}
                                disabled={isEmpty(options)}
                                className="w-full"
                            />
                        </div>
                    );
                })}
            </div>

            <div className="flex flex-col sm:flex-row xs:flex-wrap sm:items-center">
                <div className="flex w-full mb-[16px] xs:mr-8 sm:mb-0 sm:w-[fit-content]">
                    <Chips
                        id="CouponsPersonalCheckbox"
                        label={t('Personal coupons')}
                        checked={isPersonal}
                        onChange={value => onChange('isPersonal', value)}
                        icon={
                            isPersonal ? (
                                <FontAwesomeIcon icon={faCheck} className="ml-[5px]" />
                            ) : (
                                <FontAwesomeIcon icon={faCrown} />
                            )
                        }
                        className="justify-center w-full sm:w-[inherit]"
                    />
                </div>

                <div className="flex items-center justify-center w-full gap-[8px] text-[14px] leading-[21px] sm:w-[fit-content]">
                    <p className={withPromocode ? `text-blueGray-500` : `text-blueGray-800`}>
                        {t('All')}
                    </p>
                    <Switch
                        id="CouponsWithPromocode"
                        checked={withPromocode}
                        onChange={value => onChange('withPromocode', value)}
                    />
                    <p className={withPromocode ? `text-blueGray-800` : `text-blueGray-500`}>
                        {t('With promocodes')}
                    </p>
                </div>

                <Button
                    id="CouponsResetButton"
                    className="w-full mt-[16px] sm:ml-auto sm:mt-0 lg:mt-0 sm:w-[fit-content]"
                    icon={<FontAwesomeIcon icon={faTimes} />}
                    iconPosition="start"
                    onClick={resetFilters}
                >
                    <p className="hidden xs:block sm:hidden lg:block">{t('Clear filters')}</p>
                    <p className="block xs:hidden sm:block md:block lg:hidden">{t('Clear')}</p>
                </Button>
            </div>
        </div>
    );
};

export default Filters;
